import React, { useRef, useState } from "react";
import { Upload, FileText, Download, Trash2, Quote, ShoppingBag, Brush, Receipt } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 5 — Vendor Files Panel.
 *
 * Staff uploads vendor-side documents into one of four buckets:
 *   - vendor_quote   (the quote from the vendor)
 *   - vendor_po      (the purchase order sent back)
 *   - vendor_artwork (logo / embroidery artwork files)
 *   - vendor_invoice (the final invoice)
 *
 * Files are stored via the existing /uploads/photo endpoint (which now also
 * accepts PDFs and images) and then linked to the order via
 * POST /admin/orders/{id}/vendor-files with a chosen attachment_type.
 */
const BUCKETS = [
  { key: "vendor_quote",   label: "Vendor Quote",   icon: Quote,       accept: ".pdf,image/*" },
  { key: "vendor_po",      label: "Purchase Order", icon: ShoppingBag, accept: ".pdf,image/*" },
  { key: "vendor_artwork", label: "Artwork",        icon: Brush,       accept: ".pdf,image/*" },
  { key: "vendor_invoice", label: "Invoice",        icon: Receipt,     accept: ".pdf,image/*" },
];

export default function VendorFilesPanel({ order, onUpdated }) {
  const files = order.vendor_files || [];
  const [busyBucket, setBusyBucket] = useState(null);
  const [error, setError] = useState("");
  const inputs = useRef({});

  const uploadFor = async (bucketKey, file) => {
    setError("");
    setBusyBucket(bucketKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: photo } = await api.post("/uploads/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data: fresh } = await api.post(`/admin/orders/${order.order_id}/vendor-files`, {
        photo_id: photo.photo_id,
        attachment_type: bucketKey,
        label: file.name,
      });
      onUpdated?.(fresh);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyBucket(null);
    }
  };

  const remove = async (photoId) => {
    if (!window.confirm("Remove this file from the order?")) return;
    setError("");
    try {
      const { data } = await api.delete(`/admin/orders/${order.order_id}/vendor-files/${photoId}`);
      onUpdated?.(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <section className="surface-card p-7" data-testid="vendor-files-panel">
      <div className="overline">Pro Shop</div>
      <h3 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Vendor Files</h3>
      <p className="text-xs text-[#7A7A7A] mt-1">Quotes, purchase orders, artwork and invoices — all attached to this order.</p>

      {error && (
        <div className="mt-3 px-3 py-2 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">{error}</div>
      )}

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {BUCKETS.map((b) => {
          const here = files.filter((f) => f.attachment_type === b.key);
          const Icon = b.icon;
          const busy = busyBucket === b.key;
          return (
            <div key={b.key} className="border border-[#E5E3D8] bg-white p-4" data-testid={`vendor-bucket-${b.key}`}>
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2 text-[#1A422D]">
                  <Icon size={16} />
                  <span className="font-serif text-base">{b.label}</span>
                  {here.length > 0 && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-[#1A422D] bg-[#E9F0EB]">{here.length}</span>
                  )}
                </div>
                <input
                  ref={(el) => { if (el) inputs.current[b.key] = el; }}
                  type="file"
                  accept={b.accept}
                  hidden
                  data-testid={`vendor-input-${b.key}`}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadFor(b.key, f);
                    e.target.value = "";  // allow re-uploading the same file
                  }}
                />
                <button
                  type="button"
                  onClick={() => inputs.current[b.key]?.click()}
                  disabled={busy}
                  data-testid={`vendor-upload-${b.key}`}
                  className="text-xs text-[#1A422D] underline underline-offset-4 inline-flex items-center gap-1"
                >
                  <Upload size={12} /> {busy ? "Uploading…" : "Upload"}
                </button>
              </div>
              {here.length === 0 ? (
                <p className="text-xs text-[#9A9A9A] mt-3">No files yet.</p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {here.map((f) => (
                    <li key={f.photo_id} className="flex items-center justify-between gap-2 text-sm border-t border-[#E5E3D8] pt-2 first:border-0 first:pt-0">
                      <div className="min-w-0 flex items-center gap-2">
                        <FileText size={14} className="text-[#7A7A7A] flex-shrink-0" />
                        <span className="truncate text-[#1A1A1A]" title={f.label || f.original_filename}>{f.label || f.original_filename}</span>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <a
                          href={`${API_BASE}/api/files/${f.photo_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`vendor-download-${f.photo_id}`}
                          className="text-xs text-[#1A422D] inline-flex items-center gap-1"
                          title="Download"
                        >
                          <Download size={12} />
                        </a>
                        <button
                          type="button"
                          onClick={() => remove(f.photo_id)}
                          data-testid={`vendor-remove-${f.photo_id}`}
                          className="text-xs text-[#8a1a1a]"
                          title="Remove"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
